import * as THREE from 'three';
import { Loop } from '../core/Loop';
import { createRenderer, resizeRenderer } from '../core/Renderer';
import { C, OreKind, PLANETS, gravityScaleAt } from './constants';
import { World, BlockKind } from './World';
import { Ship } from '../entities/Ship';
import { Miner } from '../entities/Miner';
import { Mothership } from '../entities/Mothership';
import { Particles } from '../systems/Particles';
import { CameraRig } from '../systems/CameraRig';
import { Hud, UpgradeId } from '../systems/Hud';

type Phase = 'DOCKED' | 'DESCENT' | 'SURFACE' | 'MINING' | 'ASCENT'
  | 'DOCKING' | 'MOTHERSHIP' | 'SPACE';

/**
 * Planet Descent Miner — the loop:
 * drop from the mothership, land, laser down through strata for ore while
 * energy drains, haul it back to the ship, fly up, sell, upgrade, go deeper.
 */
export class Game {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly rig: CameraRig;
  private readonly loop: Loop;
  private planetIndex = 0;
  private world = new World(PLANETS[0]);
  private readonly ship = new Ship();
  private readonly miner = new Miner();
  private readonly mothership = new Mothership();
  private readonly particles = new Particles();
  private readonly hud: Hud;
  private readonly ambient = new THREE.AmbientLight(0xffffff, 0.75);
  private readonly sun = new THREE.DirectionalLight(0xfff4e0, 1.0);

  private phase: Phase = 'DOCKED';
  private credits = 0;
  private energy = C.energy.max;
  private readonly cargo = new Map<OreKind, number>();
  private levels: Record<UpgradeId, number> = { laser: 1, energy: 1, cargo: 1, thrust: 1 };

  // input
  private readonly keys = new Set<string>();
  private readonly joy = new THREE.Vector2();   // touch joystick, y up
  private mouseNdc = new THREE.Vector2();
  private mouseDown = false;
  private readonly ray = new THREE.Raycaster();
  private readonly aimPlane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);

  private laserHeat = 0;   // for hit feedback cadence
  private canDock = false;
  private dockT = 0;
  private dockFrom = new THREE.Vector3();
  private dockFromAngle = 0;
  private dockBeam: THREE.Mesh | null = null;
  private liftoffArmed = false;   // boost must be released post-landing before liftoff
  private readonly bombs: { mesh: THREE.Mesh; vel: THREE.Vector2 }[] = [];
  private bombCooldown = 0;
  private static readonly bombGeo = new THREE.SphereGeometry(0.45, 10, 8);
  private static readonly bombMat = new THREE.MeshLambertMaterial({ color: 0x22262e });

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.renderer = createRenderer(canvas);
    this.rig = new CameraRig(canvas.clientWidth / Math.max(1, canvas.clientHeight));
    this.hud = new Hud(document.body);
    this.loop = new Loop(this.update, this.render);

    this.scene.add(this.ambient, this.sun);
    this.sun.position.set(30, 80, 40);

    this.world.build();
    this.scene.add(this.world.group, this.ship.group, this.miner.group,
      this.mothership.group, this.particles.group);

    this.miner.group.visible = false;
    this.enterDocked(true);

    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', () => this.keys.clear());
    canvas.addEventListener('pointermove', this.onPointerMove);
    canvas.addEventListener('pointerdown', () => (this.mouseDown = true));
    window.addEventListener('pointerup', () => (this.mouseDown = false));

    this.hud.onSell = () => this.sellCargo();
    this.hud.onLaunch = () => this.launchDescent();
    this.hud.onBuy = (id) => this.buyUpgrade(id);
    this.hud.onPilot = () => this.enterMothershipPilot();
    this.hud.onVirtualKey = (code, down) => this.setVirtualKey(code, down);
    this.hud.onRefuel = () => {
      const missing = Math.ceil(C.fuel.max - this.fuel);
      if (missing <= 0) { this.hud.toast('Tank is full.', 1.2); return; }
      const cost = missing * C.fuel.costPerUnit;
      if (this.credits < cost) {
        this.hud.toast(`Refuel costs ${cost} — not enough credits`, 2);
        return;
      }
      this.credits -= cost;
      this.fuel = C.fuel.max;
      this.hud.toast(`Refueled for ${cost} credits`, 2);
      this.hud.showDock(this.cargoValue, this.levels, this.credits);
    };
    this.hud.onJoystick = (x, y) => this.joy.set(x, y);

    this.installDiagnostics();
  }

  start(): void { this.loop.start(); }

  dispose(): void {
    this.loop.stop();
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    this.renderer.dispose();
  }

  // ---------- input ----------

  private readonly onKeyDown = (e: KeyboardEvent) => {
    this.keys.add(e.code);
    if (e.code === 'KeyE') this.handleInteract();
    if (e.code === 'KeyB') this.tryDropBomb();
    if (e.code === 'KeyM') {
      if (this.phase === 'DOCKED') this.enterMothershipPilot();
      else if (this.phase === 'MOTHERSHIP') this.exitMothershipPilot();
    }
    if (e.code === 'Space' && this.phase === 'SPACE') this.fireCannon();
    if (e.code === 'Space') e.preventDefault();
  };
  private readonly onKeyUp = (e: KeyboardEvent) => this.keys.delete(e.code);

  private readonly onPointerMove = (e: PointerEvent) => {
    const r = this.canvas.getBoundingClientRect();
    this.mouseNdc.set(
      ((e.clientX - r.left) / r.width) * 2 - 1,
      -(((e.clientY - r.top) / r.height) * 2 - 1),
    );
  };

  /** Touch buttons feed the same key set as the keyboard, plus one-shot
   *  actions that keydown would normally trigger. */
  setVirtualKey(code: string, down: boolean): void {
    if (down) {
      this.keys.add(code);
      if (code === 'KeyE') this.handleInteract();
      if (code === 'KeyB') this.tryDropBomb();
      if (code === 'KeyM') {
        if (this.phase === 'DOCKED') this.enterMothershipPilot();
        else if (this.phase === 'MOTHERSHIP') this.exitMothershipPilot();
      }
      if (code === 'Space' && this.phase === 'SPACE') this.fireCannon();
    } else {
      this.keys.delete(code);
    }
  }

  private axis(neg: string[], pos: string[]): number {
    let v = 0;
    if (neg.some((k) => this.keys.has(k))) v -= 1;
    if (pos.some((k) => this.keys.has(k))) v += 1;
    return v;
  }

  // ---------- economy ----------

  private get cargoCount(): number {
    let n = 0;
    this.cargo.forEach((v) => (n += v));
    return n;
  }
  private get cargoMax(): number {
    return C.cargo.max + (this.levels.cargo - 1) * C.upgrades.cargoPerLevel;
  }
  private get energyMax(): number {
    return C.energy.max + (this.levels.energy - 1) * C.upgrades.energyPerLevel;
  }
  private get laserDps(): number {
    return C.miner.laserDps + (this.levels.laser - 1) * C.upgrades.laserDpsPerLevel;
  }
  private get thrustBonus(): number {
    return (this.levels.thrust - 1) * C.upgrades.thrustPerLevel;
  }
  private get cargoValue(): number {
    let v = 0;
    this.cargo.forEach((n, kind) => (v += n * C.ore[kind].value));
    return v;
  }

  private sellCargo(): void {
    const v = this.cargoValue;
    if (v <= 0) return;
    this.credits += v;
    this.cargo.clear();
    this.hud.toast(`Sold cargo for ${v} credits`);
    this.hud.showDock(0, this.levels, this.credits);
  }

  private buyUpgrade(id: UpgradeId): void {
    const level = this.levels[id];
    if (level >= 5) return;
    const cost = C.upgrades.costs[level - 1];
    if (this.credits < cost) return;
    this.credits -= cost;
    this.levels[id] = level + 1;
    if (id === 'energy') this.energy = this.energyMax;
    this.hud.toast(`${id.toUpperCase()} upgraded to L${level + 1}`);
    this.hud.showDock(this.cargoValue, this.levels, this.credits);
  }

  // ---------- phase transitions ----------

  /** The docking scene: tractor beam grabs the lander, rights it, and draws
   *  it up into the glowing bay. Controls are out of your hands. */
  private startDocking(): void {
    this.phase = 'DOCKING';
    this.canDock = false;
    this.dockT = 0;
    this.dockFrom.copy(this.ship.pos);
    this.dockFromAngle = this.ship.uprightError;
    this.ship.vel.set(0, 0);
    this.rig.setZoom(42);

    const beamMat = new THREE.MeshBasicMaterial({
      color: 0x39e6ff, transparent: true, opacity: 0.35,
      blending: THREE.AdditiveBlending, depthWrite: false,
    });
    this.dockBeam = new THREE.Mesh(new THREE.ConeGeometry(4.5, 1, 12, 1, true), beamMat);
    this.scene.add(this.dockBeam);
    this.mothership.setBay(true);
    this.hud.toast('Tractor lock — docking…', 2);
  }

  private updateDocking(dt: number): void {
    this.dockT += dt;
    const p = Math.min(1, this.dockT / 2.6);
    const eased = p * p * (3 - 2 * p);

    const bay = this.mothership.pos.clone().add(new THREE.Vector3(-2, -4.6, 0));
    this.ship.pos.lerpVectors(this.dockFrom, bay, eased);
    this.ship.setAttitude(THREE.MathUtils.lerp(this.dockFromAngle, 0, Math.min(1, p * 1.6)));

    // Beam stretches from the bay mouth down to the lander, pulsing
    if (this.dockBeam) {
      const top = this.mothership.pos.clone().add(new THREE.Vector3(-2, -3.2, 0));
      const len = Math.max(1, top.y - this.ship.pos.y + 1.5);
      this.dockBeam.position.set(top.x, top.y - len / 2, 0);
      this.dockBeam.scale.set(1, len, 1);
      (this.dockBeam.material as THREE.MeshBasicMaterial).opacity =
        0.25 + Math.sin(this.dockT * 12) * 0.12;
    }
    if (Math.random() < dt * 18)
      this.particles.burst(this.ship.pos.clone(), 0x39e6ff, 2, 3, 2, 0.4, 0.7);

    if (p >= 1) {
      if (this.dockBeam) {
        this.scene.remove(this.dockBeam);
        this.dockBeam = null;
      }
      this.rig.impulse(0.15);
      this.mothership.setBay(false);
      this.enterDocked();
    }
  }

  private enterDocked(first = false): void {
    this.phase = 'DOCKED';
    this.ship.vel.set(0, 0);
    this.ship.pos.copy(this.mothership.pos).add(new THREE.Vector3(0, -5, 0));
    this.rig.setZoom(C.camera.descentZ);
    if (first) this.rig.snapTo(this.ship.pos);
    this.energy = this.energyMax;
    this.hud.showDock(this.cargoValue, this.levels, this.credits);
    this.hud.toast(first ? 'Welcome aboard. Sell, upgrade, then drop.' : 'Docked.');
  }

  private enterMothershipPilot(): void {
    if (this.phase !== 'DOCKED') return;
    this.phase = 'MOTHERSHIP';
    this.hud.hideDock();
    this.rig.setZoom(95);
    this.hud.toast('Capital ship helm — she is heavy. Plan your burns.', 3);
  }

  private exitMothershipPilot(): void {
    if (this.phase !== 'MOTHERSHIP') return;
    this.mothership.haltVisuals();
    this.enterDocked();
  }

  private launchDescent(): void {
    this.hud.hideDock();
    this.mothership.pulseBay(3);
    this.phase = 'DESCENT';
    this.ship.takeOffReset();
    this.ship.vel.y = -4;
    this.rig.setZoom(C.camera.descentZ);
  }

  private handleInteract(): void {
    if (this.phase === 'ASCENT' && this.canDock) {
      this.startDocking();
      return;
    }
    if (this.phase === 'SURFACE') {
      // exit the ship
      this.phase = 'MINING';
      this.miner.group.visible = true;
      this.miner.pos.set(
        this.ship.pos.x + C.miner.exitOffset, this.ship.pos.y, 0);
      this.miner.vel.set(0, 0);
      this.rig.setZoom(C.camera.z);
    } else if (this.phase === 'MINING' && this.nearShip()) {
      // board: deposit handled implicitly (cargo is global), recharge starts
      this.phase = 'SURFACE';
      this.liftoffArmed = false;
      this.miner.group.visible = false;
      this.rig.setZoom(C.camera.descentZ * 0.75);
      this.hud.toast('Aboard. SPACE to lift off — fly up to the mothership.');
    }
  }

  private nearShip(): boolean {
    return this.miner.pos.distanceTo(this.ship.pos) < C.miner.boardRange + 1.2;
  }

  // ---------- update ----------

  private readonly update = (dt: number): void => {
    switch (this.phase) {
      case 'DOCKED': break;
      case 'DESCENT': this.updateDescent(dt); break;
      case 'SURFACE': this.updateSurface(dt); break;
      case 'MINING': this.updateMining(dt); break;
      case 'ASCENT': this.updateAscent(dt); break;
      case 'DOCKING': this.updateDocking(dt); break;
      case 'MOTHERSHIP': this.updateMothership(dt); break;
      case 'SPACE': this.updateSpace(dt); break;
    }
    this.mothership.tick(dt);

    // The docked lander rides along with the capital ship
    if (this.phase === 'DOCKED' || this.phase === 'MOTHERSHIP')
      this.ship.pos.copy(this.mothership.pos).add(new THREE.Vector3(0, -5, 0));

    this.updateBombs(dt);

    // Ore drops always simulate (they exist while mining)
    const attract = this.phase === 'MINING' ? this.miner.pos : null;
    this.particles.update(
      dt, attract,
      (kind) => this.tryCollect(kind),
      (x, y) => this.world.solidAtWorld(x, y),
    );

    const focus = this.phase === 'MINING' ? this.miner.pos
      : this.phase === 'MOTHERSHIP' || this.phase === 'SPACE' ? this.mothership.pos
      : this.ship.pos;
    const lookAhead = this.phase === 'DESCENT' ? -8 : this.phase === 'MINING' ? -2 : 0;
    this.rig.update(dt, focus, lookAhead);
    this.world.updateSky(focus.y, this.ambient, this.sun);

    this.hud.update({
      energy: this.energy,
      energyMax: this.energyMax,
      fuel: this.fuel,
      fuelMax: C.fuel.max,
      cargo: this.cargoCount,
      cargoMax: this.cargoMax,
      credits: this.credits,
      depth: Math.max(0, Math.round(-(this.phase === 'MINING' ? this.miner.pos.y : this.ship.pos.y))),
      prompt: this.promptFor(),
      phase: this.phase,
      interact: this.canDock,
    }, dt);
  };

  private promptFor(): string {
    switch (this.phase) {
      case 'DESCENT': return 'A/D rotate · W boost · S retro · B bomb — land gently, upright';
      case 'SURFACE': return 'E: exit ship · W: lift off to mothership';
      case 'MINING': {
        if (this.nearShip()) return 'E: board ship · ship recharges energy';
        return 'A/D move · SPACE jetpack · hold MOUSE to laser blocks';
      }
      case 'ASCENT': return this.canDock
        ? 'IN DOCK RANGE — E: dock with the rig'
        : 'Hold W to climb · S retro-brake · A/D rotate — reach the mothership';
      case 'DOCKING': return 'Docking…';
      case 'MOTHERSHIP': {
        if (this.mothership.landed)
          return 'Rig is DOWN · ▲ lifts her off · M: dock';
        return '◀ ▶ ▲ ▼ fly the rig · climb past orbit to break into space · M: dock';
      }
      case 'SPACE': return this.hudApproach
        ? `Approaching ${this.hudApproach} — keep closing to dive`
        : '◀ ▶ ▲ ▼ fly · TAP: shoot rocks (scrap) · dive at a world';
      default: return '';
    }
  }

  private updateDescent(dt: number): void {
    // A/D or stick-x rotate; W/SPACE or stick-up burns; S or stick-down retro
    const rotate = THREE.MathUtils.clamp(
      this.axis(['KeyD', 'ArrowRight'], ['KeyA', 'ArrowLeft']) - this.joy.x, -1, 1);
    const burn = this.keys.has('KeyW') || this.keys.has('Space')
      || this.keys.has('ArrowUp') || this.joy.y > 0.35;
    const retro = this.keys.has('KeyS') || this.keys.has('ArrowDown') || this.joy.y < -0.35;
    this.checkGravityZone();
    const result = this.ship.fly(dt, rotate, burn, retro, this.world, this.thrustBonus);
    if (burn) this.emitShipFlame();

    if (result) {
      this.phase = 'SURFACE';
      this.liftoffArmed = false;
      this.rig.setZoom(C.camera.descentZ * 0.75);
      const dustAt = this.ship.pos.clone().add(new THREE.Vector3(0, -1.5, 0));
      this.particles.burst(dustAt, 0xb9a67e, 22, 8, -4, 0.9, 1.4);
      if (result === 'landed-hard') {
        this.rig.impulse(0.9);
        this.energy = Math.max(0, this.energy - C.ship.hardLandEnergyCost);
        this.hud.toast(`Hard landing! -${C.ship.hardLandEnergyCost} energy`);
      } else {
        this.rig.impulse(0.25);
        this.hud.toast('Touchdown.');
      }
    }
  }

  private updateSurface(dt: number): void {
    // Parked; recharge; a FRESH SPACE press lifts off (the landing burn key
    // must be released first, or touchdown instantly bounces into ascent).
    this.energy = Math.min(this.energyMax, this.energy + C.energy.rechargeRate * dt);
    const boost = this.keys.has('KeyW') || this.keys.has('Space')
      || this.keys.has('ArrowUp') || this.joy.y > 0.5;
    if (!boost) this.liftoffArmed = true;
    if (this.liftoffArmed && boost) {
      this.liftoffArmed = false;
      this.phase = 'ASCENT';
      this.ship.takeOffReset();
      this.rig.setZoom(C.camera.descentZ);
    }
  }

  private updateAscent(dt: number): void {
    const rotate = THREE.MathUtils.clamp(
      this.axis(['KeyD', 'ArrowRight'], ['KeyA', 'ArrowLeft']) - this.joy.x, -1, 1);
    const burn = this.keys.has('KeyW') || this.keys.has('Space')
      || this.keys.has('ArrowUp') || this.joy.y > 0.35;
    const retro = this.keys.has('KeyS') || this.keys.has('ArrowDown') || this.joy.y < -0.35;
    this.checkGravityZone();
    // Reuse flight; ignore landing while climbing fast
    const result = this.ship.fly(dt, rotate, burn, retro, this.world, this.thrustBonus);
    if (burn) this.emitShipFlame();
    if (result && this.ship.vel.y <= 0 && this.ship.pos.y < 10) {
      // came back down — that's fine, we're on the surface again
      this.phase = 'SURFACE';
      this.liftoffArmed = false;
    }
    // In dock range: offer the dock (player triggers it — never automatic)
    this.canDock = this.ship.pos.distanceTo(this.mothership.pos) < 24;
    if (this.ship.pos.y > C.orbit.altitude + 14) {
      this.ship.pos.y = C.orbit.altitude + 14;   // soft ceiling
      this.ship.vel.y = Math.min(this.ship.vel.y, 0);
    }
  }

  private updateMothership(dt: number): void {
    // Ship-style: stick X = main-engine throttle, stick Y = pitch the nose
    // (near the deck, pushing up fires the belly lift-jets for takeoff)
    let moveX = THREE.MathUtils.clamp(
      this.axis(['KeyA', 'ArrowLeft'], ['KeyD', 'ArrowRight']) + this.joy.x, -1, 1);
    let moveY = THREE.MathUtils.clamp(
      this.axis(['KeyS', 'ArrowDown'], ['KeyW', 'ArrowUp']) + this.joy.y, -1, 1);
    this.checkGravityZone(this.mothership.pos.y);
    ({ moveX, moveY } = this.applyFuel(dt, moveX, moveY));

    // Punching out of the well: climb past orbit with a real fuel reserve
    if (this.mothership.pos.y > C.orbit.altitude + 18) {
      if (this.fuel >= C.fuel.breakOrbitMin) {
        this.enterSpace();
        return;
      }
      this.mothership.pos.y = C.orbit.altitude + 18;
      if (this.mothership.vel.y > 0) this.mothership.vel.y = 0;
      this.hud.toast(`Not enough fuel to break orbit (need ${C.fuel.breakOrbitMin})`, 2);
    }

    const entryHeat = this.mothership.fly(dt, moveX, moveY, this.world);
    if (entryHeat) {
      // Plasma sheath: streaks off the hull's leading (bottom) edge + rumble
      this.rig.impulse(0.12);
      if (Math.random() < dt * 40) {
        const at = this.mothership.pos.clone().add(new THREE.Vector3(
          (Math.random() - 0.5) * 24, -3.5, (Math.random() - 0.5) * 4));
        this.particles.burst(at, Math.random() < 0.5 ? 0xff8a2a : 0xffd070,
          2, 7, 8, 0.5, 1.4);
      }
    }
  }

  private updateMining(dt: number): void {
    if (this.recovery) {
      this.updateRecovery(dt);
      this.miner.showLaser(null, this.mouseNdc);
      return;
    }
    const moveX = THREE.MathUtils.clamp(
      this.axis(['KeyA', 'ArrowLeft'], ['KeyD', 'ArrowRight']) + this.joy.x, -1, 1);
    const jet = this.keys.has('Space') || this.keys.has('KeyW')
      || this.keys.has('ArrowUp') || this.joy.y > 0.35;

    this.miner.step(dt, moveX, jet, this.world);

    // Energy drains
    let drain = C.energy.ambientDrain;
    if (jet) drain += C.energy.jetpackDrain;

    // Laser
    let laserTarget: THREE.Vector2 | null = null;
    if (this.mouseDown) {
      laserTarget = this.fireLaser(dt);
      if (laserTarget) drain += C.energy.laserDrain;
    }
    this.miner.showLaser(laserTarget, this.mouseNdc);

    // Near ship: recharge instead of drain
    if (this.nearShip()) {
      this.energy = Math.min(this.energyMax, this.energy + C.energy.rechargeRate * dt);
    } else {
      this.energy = Math.max(0, this.energy - drain * dt);
      if (this.energy <= 0) this.startRecovery();
    }

    if (jet) {
      const at = this.miner.pos.clone().add(new THREE.Vector3(0, -0.9, 0));
      if (Math.random() < dt * 30)
        this.particles.burst(at, 0xffa040, 1, 2.5, -6, 0.35, 0.7);
    }
  }

  /** Raycast the mouse into the world plane, then march to the first block. */
  private fireLaser(dt: number): THREE.Vector2 | null {
    this.ray.setFromCamera(this.mouseNdc, this.rig.camera);
    const hit = new THREE.Vector3();
    if (!this.ray.ray.intersectPlane(this.aimPlane, hit)) return null;

    const ox = this.miner.pos.x, oy = this.miner.pos.y + 0.3;
    const dir = new THREE.Vector2(hit.x - ox, hit.y - oy);
    const dist = Math.min(dir.length(), C.miner.laserRange);
    if (dist < 0.1) return null;
    dir.normalize();

    // March along the beam to the first solid block
    for (let d = 0.4; d <= dist; d += 0.25) {
      const x = ox + dir.x * d, y = oy + dir.y * d;
      if (this.world.solidAtWorld(x, y)) {
        const col = this.world.xToCol(x), row = this.world.yToRow(y);
        const destroyed = this.world.damage(col, row, this.laserDps * dt);
        // Spark cadence
        this.laserHeat += dt;
        if (this.laserHeat > 0.06) {
          this.laserHeat = 0;
          this.particles.burst(new THREE.Vector3(x, y, 0.2), 0xffa060, 2, 4, -8, 0.3, 0.6);
        }
        if (destroyed) this.onBlockDestroyed(destroyed, col, row);
        return new THREE.Vector2(x, y);
      }
    }
    return new THREE.Vector2(ox + dir.x * dist, oy + dir.y * dist);
  }

  private onBlockDestroyed(kind: BlockKind, col: number, row: number): void {
    const at = new THREE.Vector3(this.world.colToX(col), this.world.rowToY(row), 0);
    this.rig.impulse(0.18);

    switch (kind) {
      case 'gas': {
        // Pocket detonates: knockback + energy hit + collateral damage
        this.particles.burst(at, 0x9fe06a, 26, 12, -2, 0.8, 1.5);
        this.rig.impulse(0.8);
        const away = new THREE.Vector2(
          this.miner.pos.x - at.x, this.miner.pos.y - at.y).normalize();
        this.miner.vel.x += away.x * 18;
        this.miner.vel.y += away.y * 14;
        this.energy = Math.max(1, this.energy - 10);
        this.hud.toast('Gas pocket! -10 energy');
        for (const [dc, dr] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const)
          this.world.damage(col + dc, row + dr, 2);
        break;
      }
      case 'lava':
        this.particles.burst(at, 0xff5a1c, 20, 10, -6, 0.9, 1.3);
        this.rig.impulse(0.6);
        this.energy = Math.max(1, this.energy - 14);
        this.hud.toast('Magma flash! -14 energy');
        break;
      case 'soil':
      case 'rock':
      case 'deepRock':
        this.particles.burst(at, kind === 'soil' ? 0x8a6f4d : 0x5a5347, 7, 6, -18, 0.5, 1);
        break;
      default: {
        // Ore block: debris + a physical drop that magnets to you
        const ore = kind as OreKind;
        this.particles.burst(at, C.ore[ore].glow, 10, 7, -14, 0.6, 1);
        this.particles.spawnDrop(at, ore);
      }
    }
  }

  private tryCollect(kind: OreKind): boolean {
    if (this.cargoCount >= this.cargoMax) {
      this.hud.toast('Cargo full — return to the ship', 1.2);
      return false;
    }
    this.cargo.set(kind, (this.cargo.get(kind) ?? 0) + 1);
    this.hud.toast(`+1 ${this.hud.oreName(kind)} (${C.ore[kind].value}cr)`, 1.0);
    return true;
  }

  // ---------- recovery sequence (energy depleted on foot) ----------

  private recovery: {
    stage: 'flyout' | 'tow';
    t: number;
    drone: THREE.Group;
    failPoint: THREE.Vector3;
  } | null = null;

  private startRecovery(): void {
    if (this.recovery) return;
    const failPoint = this.miner.pos.clone();

    // HALF the pouch spills as free-floating pieces at the failure point —
    // they stay there; come back with a charged suit and pick them up.
    let lost = 0;
    const target = Math.ceil(this.cargoCount * C.energy.emergencyOreLoss);
    for (const [kind, n] of this.cargo) {
      if (lost >= target) break;
      const take = Math.min(n, target - lost);
      this.cargo.set(kind, n - take);
      for (let i = 0; i < take; i++)
        this.particles.spawnDrop(
          failPoint.clone().add(new THREE.Vector3((Math.random() - 0.5) * 2, 0.5, 0)),
          kind);
      lost += take;
    }

    // The drone: a little welded utility box with a blinker
    const drone = new THREE.Group();
    const body = new THREE.Mesh(
      new THREE.BoxGeometry(0.9, 0.6, 0.6),
      new THREE.MeshLambertMaterial({ color: 0x8a5a33 }));
    const blinker = new THREE.Mesh(
      new THREE.SphereGeometry(0.16, 6, 5),
      new THREE.MeshBasicMaterial({ color: 0xff5a3a }));
    blinker.position.y = 0.5;
    const lamp = new THREE.PointLight(0xffc860, 6, 9);
    drone.add(body, blinker, lamp);
    drone.position.copy(this.ship.pos);
    this.scene.add(drone);

    this.recovery = { stage: 'flyout', t: 0, drone, failPoint };
    this.hud.toast(`SUIT DEAD — recovery drone launched. ${lost} ore spilled here.`, 3.5);
    this.rig.impulse(0.4);
  }

  private updateRecovery(dt: number): void {
    const r = this.recovery!;
    r.t += dt;
    if (r.stage === 'flyout') {
      const p = Math.min(1, r.t / C.energy.droneFlyTime);
      const eased = p * p * (3 - 2 * p);
      r.drone.position.lerpVectors(this.ship.pos, r.failPoint, eased);
      r.drone.position.y += Math.sin(p * Math.PI) * 6;   // arc over terrain
      if (p >= 1) {
        r.stage = 'tow';
        r.t = 0;
        this.hud.toast('Hooked on — hauling you back…', 2);
      }
    } else {
      const p = Math.min(1, r.t / C.energy.droneTowTime);
      const eased = p * p * (3 - 2 * p);
      const target = this.ship.pos.clone().add(new THREE.Vector3(C.miner.exitOffset, 0, 0));
      this.miner.pos.lerpVectors(r.failPoint, target, eased);
      this.miner.pos.y += Math.sin(p * Math.PI) * 6;
      r.drone.position.copy(this.miner.pos).add(new THREE.Vector3(0, 1.6, 0));
      if (Math.random() < dt * 20)
        this.particles.burst(r.drone.position.clone(), 0xffc860, 1, 2, -4, 0.3, 0.6);
      if (p >= 1) {
        this.scene.remove(r.drone);
        this.recovery = null;
        this.miner.vel.set(0, 0);
        this.energy = this.energyMax * 0.35;
        this.hud.toast('Back at the ship. Suit at 35%. Your ore is still out there.', 3.5);
      }
    }
  }

  // ---------- gravity zones ----------

  private gravityZone: 'full' | 'fading' | 'zero' = 'zero';   // start docked in space

  /** Toast warnings as the piloted vessel crosses gravity bands. */
  private checkGravityZone(yOverride?: number): void {
    const y = yOverride ?? this.ship.pos.y;
    const zone: typeof this.gravityZone =
      y >= C.orbit.gravityZeroAbove ? 'zero'
      : y <= C.orbit.gravityFullBelow ? 'full' : 'fading';
    if (zone === this.gravityZone) return;
    const prev = this.gravityZone;
    this.gravityZone = zone;

    if (zone === 'fading' && prev === 'full')
      this.hud.toast('⚠ LEAVING GRAVITY WELL — gravity dropping', 2.5);
    else if (zone === 'zero')
      this.hud.toast('ZERO-G — you are weightless', 2.5);
    else if (zone === 'fading' && prev === 'zero')
      this.hud.toast('⚠ ENTERING GRAVITY WELL — gravity increasing', 2.5);
    else if (zone === 'full')
      this.hud.toast('Atmosphere — full gravity', 2);
  }

  // ---------- open space ----------

  private fuel = C.fuel.max;
  private cannonCd = 0;
  private spaceGroup: THREE.Group | null = null;
  private readonly spaceRocks: { mesh: THREE.Mesh; vel: THREE.Vector2; r: number }[] = [];
  private readonly bolts: { mesh: THREE.Mesh; vel: THREE.Vector2 }[] = [];
  private spacePlanets: { mesh: THREE.Mesh; index: number }[] = [];
  private fireDir = 1;
  private outOfFuelToasted = false;

  /** Climbing hard past orbit with enough fuel punches into open space. */
  private enterSpace(): void {
    this.phase = 'SPACE';
    this.world.group.visible = false;
    this.ship.group.visible = false;
    this.rig.setZoom(230);   // way out — see the field, the rocks, the worlds

    const g = new THREE.Group();
    // Deep-space starfield
    const starGeo = new THREE.BufferGeometry();
    const pts: number[] = [];
    for (let i = 0; i < 700; i++)
      pts.push((Math.random() - 0.5) * C.space.bounds.x * 2.4,
        (Math.random() - 0.5) * C.space.bounds.y * 2.6,
        -40 - Math.random() * 25);
    starGeo.setAttribute('position', new THREE.Float32BufferAttribute(pts, 3));
    g.add(new THREE.Points(starGeo, new THREE.PointsMaterial({
      color: 0xcfe0ff, size: 1.5, sizeAttenuation: false,
      transparent: true, opacity: 0.9, depthWrite: false,
    })));

    // The worlds, hanging in the dark where they actually are
    this.spacePlanets = [];
    PLANETS.forEach((planet, i) => {
      const mesh = new THREE.Mesh(
        new THREE.SphereGeometry(26, 26, 20),
        new THREE.MeshLambertMaterial({ color: planet.skyLow }),
      );
      mesh.position.set(C.space.planets[i].x, C.space.planets[i].y, -8);
      g.add(mesh);
      this.spacePlanets.push({ mesh, index: i });
    });

    // Asteroid field: slow-drifting rocks scattered across the field
    for (let i = 0; i < C.space.asteroidCount; i++) {
      const r = 1.4 + Math.random() * 3.4;
      const rock = new THREE.Mesh(
        new THREE.DodecahedronGeometry(r, 0),
        new THREE.MeshLambertMaterial({ color: 0x6e6154 }),
      );
      rock.position.set(
        (Math.random() - 0.5) * C.space.bounds.x * 2,
        (Math.random() - 0.5) * C.space.bounds.y * 2, 0);
      rock.rotation.set(Math.random() * 3, Math.random() * 3, 0);
      g.add(rock);
      this.spaceRocks.push({
        mesh: rock,
        vel: new THREE.Vector2((Math.random() - 0.5) * 4, (Math.random() - 0.5) * 4),
        r,
      });
    }

    this.spaceGroup = g;
    this.scene.add(g);

    // Enter space just off the planet we left — camera arrives WITH the rig
    // (no visual snap), then the guided climb-out plays before control returns.
    const from = C.space.planets[this.planetIndex];
    this.mothership.pos.set(from.x, from.y + 42, 0);
    this.mothership.vel.set(0, 9);
    this.mothership.pitch = 0.5;         // nose up out of the well
    this.rig.snapTo(this.mothership.pos);
    this.spaceIntroT = 2.6;
    this.hud.toast('BREAKING ORBIT — climbing out…', 2.6);
  }

  private spaceIntroT = 0;

  /**
   * Fire the junk cannon. With `dir`, the bolt flies that way (tap-to-shoot);
   * without, it auto-aims at the nearest rock in range, falling back to a
   * straight shot along the last travel direction.
   */
  private fireCannon(dir?: THREE.Vector2): void {
    if (this.cannonCd > 0 || this.phase !== 'SPACE') return;
    this.cannonCd = C.space.cannonCooldown;

    let aim = dir;
    if (!aim) {
      let best: THREE.Vector2 | null = null;
      let bestD = 190;
      for (const rock of this.spaceRocks) {
        const d = Math.hypot(
          rock.mesh.position.x - this.mothership.pos.x,
          rock.mesh.position.y - this.mothership.pos.y);
        if (d < bestD) {
          bestD = d;
          best = new THREE.Vector2(
            rock.mesh.position.x - this.mothership.pos.x,
            rock.mesh.position.y - this.mothership.pos.y).normalize();
        }
      }
      aim = best ?? new THREE.Vector2(this.fireDir, 0);
    }

    const mesh = new THREE.Mesh(
      new THREE.SphereGeometry(1.1, 8, 6),   // chunky — readable at far zoom
      new THREE.MeshBasicMaterial({
        color: 0xffb050, blending: THREE.AdditiveBlending,
        transparent: true, depthWrite: false,
      }),
    );
    mesh.position.copy(this.mothership.pos)
      .add(new THREE.Vector3(aim.x * 17, aim.y * 17 - 1, 0));
    this.scene.add(mesh);
    this.bolts.push({
      mesh,
      vel: new THREE.Vector2(aim.x, aim.y).multiplyScalar(C.space.boltSpeed),
    });
    this.rig.impulse(0.08);
  }

  private updateSpace(dt: number): void {
    let moveX = THREE.MathUtils.clamp(
      this.axis(['KeyA', 'ArrowLeft'], ['KeyD', 'ArrowRight']) + this.joy.x, -1, 1);
    let moveY = THREE.MathUtils.clamp(
      this.axis(['KeyS', 'ArrowDown'], ['KeyW', 'ArrowUp']) + this.joy.y, -1, 1);
    if (Math.abs(moveX) > 0.15) this.fireDir = Math.sign(moveX);

    // Guided climb-out: the rig coasts up and away on rails while the camera
    // pulls back to reveal the field — control returns as the toast fades.
    if (this.spaceIntroT > 0) {
      this.spaceIntroT -= dt;
      this.mothership.flySpace(dt, 0.35, -this.mothership.pitch * 0.25);
      return;
    }

    // Fuel: thrust costs, solar trickle refills
    ({ moveX, moveY } = this.applyFuel(dt, moveX, moveY));

    this.mothership.flySpace(dt, moveX, moveY);
    this.cannonCd = Math.max(0, this.cannonCd - dt);
    if (this.keys.has('Space')) this.fireCannon();

    // Tap/click-to-shoot: hold to keep firing at the pointer
    if (this.mouseDown) {
      this.ray.setFromCamera(this.mouseNdc, this.rig.camera);
      const hit = new THREE.Vector3();
      if (this.ray.ray.intersectPlane(this.aimPlane, hit)) {
        const aim = new THREE.Vector2(
          hit.x - this.mothership.pos.x, hit.y - this.mothership.pos.y);
        if (aim.lengthSq() > 4) this.fireCannon(aim.normalize());
      }
    }

    // Rocks drift; ram = bounce + shake; bolt = scrap credits
    const m = this.mothership;
    for (let i = this.spaceRocks.length - 1; i >= 0; i--) {
      const rock = this.spaceRocks[i];
      rock.mesh.position.x += rock.vel.x * dt;
      rock.mesh.position.y += rock.vel.y * dt;
      rock.mesh.rotation.z += dt * 0.5;
      // Wrap drifting rocks at the field edge
      if (Math.abs(rock.mesh.position.x) > C.space.bounds.x * 1.2) rock.vel.x *= -1;
      if (Math.abs(rock.mesh.position.y) > C.space.bounds.y * 1.2) rock.vel.y *= -1;

      if (Math.abs(rock.mesh.position.x - m.pos.x) < 14 + rock.r &&
          Math.abs(rock.mesh.position.y - m.pos.y) < 4.5 + rock.r) {
        // Hull bounce
        this.rig.impulse(0.8);
        const away = new THREE.Vector2(
          m.pos.x - rock.mesh.position.x, m.pos.y - rock.mesh.position.y).normalize();
        m.vel.x += away.x * 10;
        m.vel.y += away.y * 10;
        rock.vel.x -= away.x * 3;
        rock.vel.y -= away.y * 3;
        this.particles.burst(rock.mesh.position.clone(), 0xffaa60, 12, 10, 0, 0.7, 1.3);
        this.hud.toast('Hull impact!', 1.2);
      }

      for (let j = this.bolts.length - 1; j >= 0; j--) {
        if (this.bolts[j].mesh.position.distanceTo(rock.mesh.position) < rock.r + 0.9) {
          this.scene.remove(this.bolts[j].mesh);
          this.bolts.splice(j, 1);
          this.scene.remove(rock.mesh);
          this.spaceRocks.splice(i, 1);

          if (rock.r > 2.1) {
            // Big rock SHATTERS into tumbling fragments
            this.particles.burst(rock.mesh.position.clone(), 0xd8c090, 10, 8, 0, 0.6, 1.1);
            const n = 2 + Math.floor(Math.random() * 2);
            for (let k = 0; k < n; k++) {
              const fr = rock.r * (0.42 + Math.random() * 0.18);
              const frag = new THREE.Mesh(
                new THREE.DodecahedronGeometry(fr, 0),
                new THREE.MeshLambertMaterial({ color: 0x6e6154 }),
              );
              const a = (k / n) * Math.PI * 2 + Math.random();
              frag.position.copy(rock.mesh.position).add(
                new THREE.Vector3(Math.cos(a) * fr * 1.6, Math.sin(a) * fr * 1.6, 0));
              frag.rotation.set(Math.random() * 3, Math.random() * 3, 0);
              this.scene.add(frag);
              this.spaceRocks.push({
                mesh: frag,
                vel: new THREE.Vector2(
                  rock.vel.x + Math.cos(a) * (5 + Math.random() * 6),
                  rock.vel.y + Math.sin(a) * (5 + Math.random() * 6)),
                r: fr,
              });
            }
            this.credits += 1;
            this.hud.toast('+1 scrap — it shattered!', 0.9);
          } else {
            // Small rock dies for real — full scrap
            this.particles.burst(rock.mesh.position.clone(), 0xd8c090, 18, 11, 0, 0.9, 1.4);
            this.credits += C.space.scrapPerRock;
            this.hud.toast(`+${C.space.scrapPerRock} scrap`, 0.9);
          }
          break;
        }
      }
    }

    for (let i = this.bolts.length - 1; i >= 0; i--) {
      const b = this.bolts[i];
      b.mesh.position.x += b.vel.x * dt;
      b.mesh.position.y += b.vel.y * dt;
      if (Math.hypot(b.mesh.position.x - m.pos.x, b.mesh.position.y - m.pos.y) > 280) {
        this.scene.remove(b.mesh);
        this.bolts.splice(i, 1);
      }
    }

    // Near a world? Dive at it (thrust toward it while close)
    for (const p of this.spacePlanets) {
      const d = Math.hypot(m.pos.x - p.mesh.position.x, m.pos.y - p.mesh.position.y);
      if (d < C.space.approachRange) {
        this.hudApproach = PLANETS[p.index].name;
        if (d < 32) this.enterPlanetFromSpace(p.index);
        return;
      }
    }
    this.hudApproach = null;
  }

  private hudApproach: string | null = null;

  /** Consume fuel for active thrust; dead thrusters when dry (solar trickle). */
  private applyFuel(dt: number, moveX: number, moveY: number)
    : { moveX: number; moveY: number } {
    const burning = Math.abs(moveX) + Math.abs(moveY);
    if (this.fuel <= 0 && burning > 0) {
      if (!this.outOfFuelToasted) {
        this.outOfFuelToasted = true;
        this.hud.toast('FUEL DRY — thrusters dead. Solar cells trickle-charging…', 3.5);
      }
      moveX = 0;
      moveY = 0;
    }
    this.fuel = Math.max(0, this.fuel - burning * C.fuel.thrustDrain * dt);
    this.fuel = Math.min(C.fuel.max, this.fuel + C.fuel.solarRegen * dt);
    if (this.fuel > 8) this.outOfFuelToasted = false;
    return { moveX, moveY };
  }

  /** Dive from open space into a world's atmosphere. */
  private enterPlanetFromSpace(index: number): void {
    if (this.spaceGroup) { this.scene.remove(this.spaceGroup); this.spaceGroup = null; }
    for (const r of this.spaceRocks) this.scene.remove(r.mesh);
    this.spaceRocks.length = 0;
    for (const b of this.bolts) this.scene.remove(b.mesh);
    this.bolts.length = 0;
    this.spacePlanets = [];

    if (index !== this.planetIndex) {
      this.particles.clearDrops();
      for (const b of this.bombs) this.scene.remove(b.mesh);
      this.bombs.length = 0;
      this.planetIndex = index;
      this.world.dispose();
      this.world = new World(PLANETS[index]);
      this.world.build();
      this.scene.add(this.world.group);
    }
    this.world.group.visible = true;

    this.mothership.pos.set(0, C.orbit.altitude + 12, 0);
    this.mothership.vel.set(0, -6);
    this.mothership.pitch = -0.3;        // nose-down entry attitude
    this.mothership.landed = false;
    this.ship.group.visible = true;
    this.gravityZone = 'zero';
    this.phase = 'MOTHERSHIP';
    this.rig.snapTo(this.mothership.pos);   // camera rides in with her
    this.rig.setZoom(95);
    this.hud.toast(`${PLANETS[index].name} — atmospheric entry. Take her down.`, 4);
  }

  // ---------- bombs ----------

  private tryDropBomb(): void {
    const flying = this.phase === 'DESCENT' || this.phase === 'ASCENT';
    if (!flying || this.bombCooldown > 0) return;
    this.bombCooldown = C.bomb.cooldown;

    const mesh = new THREE.Mesh(Game.bombGeo, Game.bombMat);
    mesh.position.copy(this.ship.pos).add(new THREE.Vector3(0, -2.4, 0));
    this.scene.add(mesh);
    this.bombs.push({
      mesh,
      vel: new THREE.Vector2(this.ship.vel.x, this.ship.vel.y + C.bomb.dropSpeed),
    });
    this.hud.toast('Bomb away!', 0.9);
  }

  private updateBombs(dt: number): void {
    this.bombCooldown = Math.max(0, this.bombCooldown - dt);
    for (let i = this.bombs.length - 1; i >= 0; i--) {
      const b = this.bombs[i];
      b.vel.y += C.ship.gravity * gravityScaleAt(b.mesh.position.y) * dt;
      b.mesh.position.x += b.vel.x * dt;
      b.mesh.position.y += b.vel.y * dt;
      b.mesh.rotation.z += dt * 6;

      if (this.world.solidAtWorld(b.mesh.position.x, b.mesh.position.y - 0.4)) {
        this.explodeBomb(b.mesh.position.clone());
        this.scene.remove(b.mesh);
        this.bombs.splice(i, 1);
      } else if (b.mesh.position.y < -C.world.rows - 5) {
        this.scene.remove(b.mesh);   // fell into the void somehow
        this.bombs.splice(i, 1);
      }
    }
  }

  /** Carve a crater; destroyed ore cells pop drops like laser mining. */
  private explodeBomb(at: THREE.Vector3): void {
    this.rig.impulse(1.0);
    this.particles.burst(at, 0xff8a2a, 34, 14, -6, 1.0, 1.8);
    this.particles.burst(at, 0x5a5347, 20, 9, -16, 0.8, 1.2);

    const r = C.bomb.radius;
    const c0 = this.world.xToCol(at.x), r0 = this.world.yToRow(at.y);
    const span = Math.ceil(r);
    for (let dc = -span; dc <= span; dc++)
      for (let dr = -span; dr <= span; dr++) {
        if (dc * dc + dr * dr > r * r) continue;
        const col = c0 + dc, row = r0 + dr;
        const block = this.world.blockAt(col, row);
        if (!block || !block.alive) continue;
        const kind = this.world.damage(col, row, 999);
        if (kind && kind !== 'rock' && kind !== 'deepRock' && kind !== 'soil')
          this.onBlockDestroyed(kind, col, row);
      }
  }

  private emitShipFlame(): void {
    if (Math.random() > 0.5) return;
    const at = this.ship.pos.clone().add(new THREE.Vector3(
      (Math.random() - 0.5) * 0.8, -2.2, 0));
    this.particles.burst(at, 0xff8a2a, 1, 3, -2, 0.4, 1);
  }

  // ---------- render / misc ----------

  private readonly render = (): void => {
    resizeRenderer(this.renderer, this.rig.camera);
    this.renderer.render(this.scene, this.rig.camera);
  };

  private installDiagnostics(): void {
    (window as unknown as Record<string, unknown>).__THREE_GAME_DIAGNOSTICS__ = {
      get state() { return null; },
    };
    Object.defineProperty(window, '__THREE_GAME_DIAGNOSTICS__', {
      value: {
        game: this,
        snapshot: () => ({
          phase: this.phase,
          credits: this.credits,
          energy: this.energy,
          cargo: this.cargoCount,
          shipY: this.ship.pos.y,
          minerY: this.miner.pos.y,
          drops: this.particles.drops.length,
          renderer: this.renderer.info.render,
        }),
      },
      configurable: true,
    });
  }
}
